import barba from '@barba/core';
import gsap from "gsap";
import Flip from "gsap/Flip";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import Draggable from "gsap/Draggable";
import InertiaPlugin from "gsap/InertiaPlugin";
import LazyLoad from "vanilla-lazyload";

// --------------------------------------------------
// 🙂 General
// --------------------------------------------------

history.scrollRestoration = 'manual';

gsap.registerPlugin(Flip, ScrollTrigger, ScrollToPlugin, Draggable, InertiaPlugin);

let mm = gsap.matchMedia();

// --------------------------------------------------
// 😴 Lazy Loading
// --------------------------------------------------

// Set up the lazy loading instance
function lazyLoading() {
  new LazyLoad({
    elements_selector: "[data-lazy]",
    callback_loaded: (element) => {

      // Fade the image in
      gsap.to(element, {
        autoAlpha: 1,
        duration: .5,
        ease: 'power1.inOut',
      })

    }
  });
}

// --------------------------------------------------
// ✨ Default Load Animation
// --------------------------------------------------

class DefaultLoadAnimation {

  constructor() {
    gsap.set('.header', {
      autoAlpha: 1
    })
  }

}

// --------------------------------------------------
// ✨ Home Load Animation
// --------------------------------------------------

class HomeLoadAnimation {

  constructor(projectTeasers = document.querySelectorAll('.project-teaser'), instant = false) {

    if(projectTeasers.length) {

      gsap.set(window, {
        scrollTo: 0
      })

      gsap.set(document.body, {
        overflow: 'hidden'
      })

      let tl = gsap.timeline({
        defaults: {
          duration: 1,
          ease: 'power1.inOut'
        },
      });

      tl.to(projectTeasers, {
        autoAlpha: 1
      });
  
      tl.fromTo('.header, .big-logo:not(.menu .big-logo) svg', {
        y: -40,
        autoAlpha: .15
      },{
        y: 0,
        autoAlpha: 1,
      }, '<');

      tl.set(document.body, {
        clearProps: 'all'
      });

      tl.to('.header', {
        duration: .75
      });
  
      projectTeasers.forEach(projectTeaser => {
        let projectTitle = projectTeaser.querySelector('.project-teaser__title');
        let projectImage = projectTeaser.querySelector('.project-teaser__image .image-wrap');
        let projectTL = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power1.inOut'
          }
        });
        projectTL.to(projectTitle, {
          opacity: 1
        });
        projectTL.to(projectImage, {
          autoAlpha: 1,
        }, '<');
        tl.add(projectTL, '-=.75');
      });
  
      if(instant) {
        tl.progress(1);
      }
      else {
        tl.play();
      }
  
    }

  }

}

// --------------------------------------------------
// 🗣️ Big Logo Animation
// --------------------------------------------------

class bigLogoAnimation {

  constructor() {

    let self = this;

    mm.add('(min-width: 701px)', () => {
  

      gsap.set('.logo', {
        autoAlpha: 0,
        yPercent: 25
      });
  
      this.tl = gsap.timeline({
        scrollTrigger: {
          start: 0,
          end: 275,
          scrub: true,
        }
      })
      this.tl.to('.big-logo:not(.menu .big-logo)', {
        y: '7.5rem',
        ease: 'none',
        duration: 3
      });
      this.tl.fromTo('.logo', {
        x: 0,
        y: 0,
        yPercent: 25,
        autoAlpha: 0,
      }, {
        x: 0,
        y: 0,
        yPercent: 0,
        autoAlpha: 1,
      }, '-=.5');
  
      return function() {
        self.kill()
      };
    
    })

  }

  kill() {
    gsap.set('.logo', {
      clearProps: 'all'
    });
    if(this.tl) {
      this.tl.kill();
    }
  }

}

// --------------------------------------------------
// 🗞️ Ticker Animation
// --------------------------------------------------

gsap.to('.ticker__wrap', {
  xPercent: -50,
  duration: 10,
  repeat: -1,
  ease: 'none',
  force3D: true
})

// --------------------------------------------------
// 🕑 Open Toggle Animation
// --------------------------------------------------

let openToggles = document.querySelectorAll('.open-toggle__dot');
openToggles.forEach(openToggle => {
  gsap.timeline({
    repeat: -1,
    repeatDelay: .25,
    yoyo: true
  })
  .fromTo(openToggle, {
    autoAlpha: 1
  }, {
    autoAlpha: .25,
    duration: .5,
  })
})

// --------------------------------------------------
// 🍔 Menu Animation
// --------------------------------------------------

let menuToggle = document.querySelector('.menu-toggle');
let menuToggleOpen = menuToggle.querySelector('.menu-toggle__text--open');
let menuToggleClose = menuToggle.querySelector('.menu-toggle__text--close');

let menu = document.querySelector('.menu');
let menuNav = menu.querySelector('.menu__nav');
let menuFooter = menu.querySelector('.menu__footer');
let menuFooterItems = menuFooter.querySelectorAll('nav a');
let menuItems = menu.querySelectorAll('.menu-item');
let menuLogo = menu.querySelector('.big-logo');

gsap.set(menuFooter, {
  yPercent: 50
})

let menuAnimation = gsap.timeline({
  paused: true,
  defaults: {
    duration: .5,
    ease: "power1.inOut",
  },
});
menuAnimation.to('.header__topbar', {
  backgroundColor: 'transparent',
}, 0)
menuAnimation.to('.logo svg', {
  autoAlpha: 0,
}, 0)
menuAnimation.to(menu, {
  autoAlpha: 1,
}, 0)
menuAnimation.to(menuToggleOpen, {
  autoAlpha: 0,
}, 0)
menuAnimation.to(menuToggleClose, {
  autoAlpha: 1,
}, 0)
menuAnimation.to(menuNav, {
  autoAlpha: 1,
}, '-=.25')
menuAnimation.to(menuFooter, {
  yPercent: 0,
  autoAlpha: 1,
}, '-=.25')

menuAnimation.reverse();

function openMenu(timeScale = 1) {
  menuAnimation.timeScale(timeScale).play();
}

function closeMenu(timeScale = 1) {
  menuAnimation.timeScale(timeScale).reverse();
}

menuToggle.addEventListener('click', (e) => {
  if(menuAnimation.reversed()) {
    openMenu();
  }
  else {
    closeMenu();
  }
  e.preventDefault();
});

menuItems.forEach(menuItem => {
  menuItem.addEventListener('click', (e) => {
    closeMenu(2);
  })
})

menuFooterItems.forEach(menuItem => {
  menuItem.addEventListener('click', (e) => {
    closeMenu(2);
  })
})

menuLogo.addEventListener('click', (e) => {
  closeMenu(2);
})

document.addEventListener('keydown', (e) => {
  if(e.key === 'Escape') {
    closeMenu(2);
  }
})

// --------------------------------------------------
// 🌎 Language Toggle
// --------------------------------------------------

let languageToggle = document.querySelector('.language-toggle');
if(languageToggle) {
  languageToggle.addEventListener('click', (e) => {
    let href = languageToggle.getAttribute('href');
    languageToggle.classList.toggle('toggle--active');
    languageToggle.classList.toggle('toggle--interacted');
    setTimeout(() => {
      window.location = href; 
    }, 330);
    e.preventDefault();
  })
}

// --------------------------------------------------
// 📂 Filter Bar
// --------------------------------------------------

const filterBar = () => {
  let filterBar = document.querySelector('.filter-bar');
  let filterToggle = document.querySelector('.filter-bar__toggle');
  if(filterToggle) {
    filterToggle.addEventListener('click', (e) => {
      filterBar.classList.toggle('filter-bar--open');
      e.preventDefault();
    })
  }
}

// --------------------------------------------------
// ✨ Filter Animation
// --------------------------------------------------

class Filter {
  constructor(tags = document.querySelectorAll('.filter-bar .tag')) {
    
    if(tags.length) {
      let allTags = document.querySelector('.filter-bar .tag--all');
      tags.forEach(tag => {
        tag.addEventListener('click', (e) => {

          if(tag == allTags) {
            tags.forEach(otherTag => {
              otherTag.classList.remove('tag--active');
            })
            tag.classList.add('tag--active');
          }
          else if(tag.classList.contains('tag--active')) {
            tag.classList.remove('tag--active');
          }
          else {
            tag.classList.add('tag--active');
          }

          if(document.querySelectorAll('.tag--active:not(.tag--all)').length) {
            allTags.classList.remove('tag--active');
          }
          else {
            allTags.classList.add('tag--active');
          }

          this.filter();

          e.preventDefault();
        });
      });
    }

  }
  filter() {
    let activeTags = [];
    let activeTagElements = document.querySelectorAll('.filter-bar .tag--active:not(.tag--all)');
    activeTagElements.forEach(activeTagElement => {
      activeTags.push(activeTagElement.getAttribute('data-tag'));
    });

    let projects = document.querySelectorAll('.project-teaser');
    let projectsState = Flip.getState(projects);
    let startHeight = gsap.getProperty(".project-teasers", "height");

    projects.forEach(project => {
      let projectTags = project.dataset.tags.split('+');

      if(!activeTags.length || projectTags.filter(value => activeTags.includes(value)).length) {
        project.classList.remove('project-teaser--filtered');
      }
      else {
        project.classList.add('project-teaser--filtered');
      }
    });

    let endHeight = gsap.getProperty(".project-teasers", "height");

    let flip = Flip.from(projectsState, {
      duration: 0.5,
      ease: "power1.inOut",
      stagger: 0.08,
      absolute: true,
      simple: true,
      onEnter: elements => gsap.fromTo(elements, {opacity: 0, scale: 0}, {opacity: 1, scale: 1, duration: .5, ease: "power1.inOut"}),
      onLeave: elements => gsap.to(elements, {opacity: 0, scale: 0, duration: .5, ease: "power1.inOut"})
    });
    
    flip.fromTo(".project-teasers", {
      height: startHeight
    }, {
      height: endHeight,
      overwrite: true,
      clearProps: "height",
      ease: "power1.inOut",
      duration: flip.duration()
    }, 0);

    if(activeTags.length) {
      history.replaceState(null, null, document.body.dataset.url + '/tag:' + activeTags.join('+'));
    }
    else {
      history.replaceState(null, null, document.body.dataset.url);
    }
  }
}

// --------------------------------------------------
// 🌄 Project Images Slider
// --------------------------------------------------

class ProjectSlider {

  constructor(wrapper) {

    let self = this;

    self.wrapper = wrapper;
    self.maxScroll = ScrollTrigger.maxScroll(window, true);
    self.projectImages = self.wrapper.querySelectorAll('.project-images__image');
    self.currentIndex = self.wrapper.querySelector('.project-thumbs__index .current');
    self.scrollTriggers = [];
    self.scrollProxy = self.wrapper.querySelector('.scroll-proxy');

    self.thumbAnimation();

    mm.add('(min-width: 1001px)', () => {

      function refreshHandler() {
        if(self.trigger) {
          self.draggable[0].applyBounds();
          updateHandler();
        }
      }
    
      function updateHandler() {
        gsap.set(self.scrollProxy, {x: document.documentElement.scrollLeft * -1});
      }

      self.draggable = Draggable.create(self.scrollProxy, {
        trigger: wrapper.querySelector('.project-main'),
        type: 'x',
        lockAxis: true,
        inertia: true,
        allowContextMenu: true,
        bounds: {
          maxX: 0, 
          minX: self.maxScroll * -1
        },
        zIndexBoost: false,
        onDragStart: function (e) {
          document.body.classList.add('dragging');
        },
        onDrag: function (e) {
          self.draggable[0].applyBounds();
          document.documentElement.scrollLeft = this.x * -1;
        },
        onThrowUpdate: function(e) {
          self.draggable[0].applyBounds();
          document.documentElement.scrollLeft = this.x * -1;
        },
        onThrowComplete: function(e) {
          document.body.classList.remove('dragging');
        }
      });
    
      self.trigger = ScrollTrigger.create({
        horizontal: true,
        onRefresh: refreshHandler,
        onUpdate: updateHandler
      });

    });

    self.scrollObserver = ScrollTrigger.observe({
      target: window,
      type: "wheel",
      onChange: (o) => {

        if (
          Math.abs(o.deltaY) > Math.abs(o.deltaX) &&
          !o.event.target.matches('.project-drawer *')
        ) {
          document.documentElement.scrollLeft += o.deltaY / 2;
        }

        // Kill the throw tween when the user starts scrolling
        if(document.body.classList.contains('dragging') && self.draggable[0].tween) {
          self.draggable[0].tween.kill();
        }

      },
    });


    document.addEventListener('keydown', (e) => {

      if(e.key === 'ArrowRight' || e.key === ' ' || e.key === 'ArrowLeft') {

        // Keep the back/forward browser navigation working
        if(e.metaKey) return;

        let offsetX = window.innerWidth / 2;
        let currentImage = self.wrapper.querySelector('.project-images__image--active');
        let targetImage;

        if(!currentImage) {
          targetImage = self.wrapper.querySelector('.project-images__image:first-child');
        }
        else {
          if(e.key === 'ArrowRight' || e.key === ' ') {
            targetImage = currentImage.nextElementSibling;
          }
          else if(e.key === 'ArrowLeft') {
            targetImage = currentImage.previousElementSibling;
          }
        }

        if(targetImage) {

          gsap.to(window, {
            scrollTo: {
              x: targetImage,
              offsetX: offsetX - targetImage.offsetWidth / 2,
            },
            ease: "power2.inOut",
            duration: 1,
          });
          e.stopPropagation();
          e.preventDefault();
        }

      }

    })

    self.projectImages.forEach(projectImage => {

      let scrollTrigger = ScrollTrigger.create({
        horizontal: true,
        trigger: projectImage,
        start: "left-=6 center",
        end: "right center",
        invalidateOnRefresh: true,
        onEnter: () => {
          self.currentIndex.innerHTML = projectImage.dataset.index;
          projectImage.classList.add('project-images__image--active');
        },
        onEnterBack: () => {
          self.currentIndex.innerHTML = projectImage.dataset.index;
          projectImage.classList.add('project-images__image--active');
        },
        onLeave: () => {
          projectImage.classList.remove('project-images__image--active');
        },
        onLeaveBack: () => {
          projectImage.classList.remove('project-images__image--active');
        },
      });

      self.scrollTriggers.push(scrollTrigger);

      projectImage.addEventListener('click', (e) => {
        gsap.to(window, {
          scrollTo: {
            x: projectImage,
            offsetX: window.innerWidth / 2 - projectImage.offsetWidth / 2,
          },
          ease: "power2.inOut",
          duration: 1,
        });
      })

      let projectThumb = self.wrapper.querySelector('.project-thumbs__image[data-index="' + projectImage.dataset.index + '"]');

      projectThumb.addEventListener('click', (e) => {
        gsap.to(window, {
          scrollTo: {
            x: projectImage,
            offsetX: window.innerWidth / 2 - projectImage.offsetWidth / 2,
          },
          ease: "power2.inOut",
          duration: 1,
        });
        e.stopPropagation();
      })

    })

  }

  thumbAnimation() {

    let self = this;

    let projectThumbs = self.wrapper.querySelectorAll('.project-thumbs__image');

    self.thumbAnimation = gsap.timeline({
      paused: true
    })

    projectThumbs.forEach(projectThumb => {

      gsap.set([projectThumb, projectThumb.querySelector('img, video')], {
        y: 0
      })

      projectThumb.addEventListener('mouseenter', e => {
        gsap.to(projectThumb, {
          y: -5
        })
      })
      projectThumb.addEventListener('mouseleave', e => {
        gsap.to(projectThumb, {
          y: 0
        })
      })

      self.thumbAnimation
        .add(
          gsap.timeline()
            .to(projectThumb.querySelector('img, video'), {
              y: -15,
              duration: 1,
              ease: 'power1.inOut',
            })
            .fromTo(projectThumb.querySelector('img, video'), {
              y: -15
            },
            {
              immediateRender: false,
              y: 0,
              duration: 1,
              ease: 'power1.inOut',
            })
        , '<.5');
    });

    self.thumbAnimation.to('.project-thumbs__index .slash', {
      flexBasis: '0',
      duration: self.thumbAnimation.duration(),
      ease: 'none',
    }, 0);

    let animationDuration = self.thumbAnimation.duration();

    self.thumbAnimationAnimation = gsap.fromTo(self.thumbAnimation, {
      progress: 1.5 / animationDuration
    }, {
      progress: 1 - 1 / animationDuration,
      ease: 'none',
      scrollTrigger: {
        trigger: self.wrapper.querySelector('.project-images__image--first'),
        endTrigger: self.wrapper.querySelector('.project-images__image--last'),
        horizontal: true,
        start: 'center center',
        end: 'center center',
        scrub: .5,
      }
    })
    
  }

  kill() {

    this.scrollObserver.kill();

    this.scrollTriggers.forEach(scrollTrigger => {
      scrollTrigger.kill();
    })

    this.thumbAnimation.kill();
    this.thumbAnimationAnimation.kill();

  }
}

// --------------------------------------------------
// 📫 Project Drawer
// --------------------------------------------------

class ProjectDrawer {

  constructor(wrapper) {

    let projectDrawer = wrapper.querySelector('.project-drawer');

    if(projectDrawer) {

      projectDrawer.addEventListener('mouseenter', () => {
        if(!projectDrawer.classList.contains('project-drawer--open')) {
          gsap.to(projectDrawer, {
            y: '-5rem',
            ease: 'power2.inOut'
          })
        }
      });

      projectDrawer.addEventListener('mouseleave', () => {
        if(!projectDrawer.classList.contains('project-drawer--open')) {
          gsap.to(projectDrawer, {
            y: '-4rem',
            ease: 'power2.inOut'
          })
        }
      });

      let animatingDrawer = false;

      let buttons = wrapper.querySelector('.project-title__buttons');

      function openDrawer() {
        animatingDrawer = true;
        gsap.to(projectDrawer, {
          yPercent: -100,
          y: 0,
          ease: 'power2.inOut',
          onComplete: () => {
            animatingDrawer = false;
          }
        })
        if(buttons) {
          gsap.to(buttons, {
            autoAlpha: 1,
            ease: 'power2.inOut'
          })
        }
      }

      function closeDrawer() {
        animatingDrawer = true;
        gsap.to(projectDrawer, {
          yPercent: 0,
          y: '-4rem',
          ease: 'power2.inOut',
          onComplete: () => {
            animatingDrawer = false;
          }
        })
        if(buttons) {
          gsap.to(buttons, {
            autoAlpha: 0,
            ease: 'power2.inOut'
          })
        }
      }

      projectDrawer.addEventListener('click', (e) => {
        if(e.target.matches('.project-drawer:not(.project-drawer--open)') || e.target.matches('.project-drawer:not(.project-drawer--open) *') || e.target.matches('.project-drawer__header, .project-drawer__header *')) {
          projectDrawer.classList.toggle('project-drawer--open');
          if(projectDrawer.classList.contains('project-drawer--open')) {
            openDrawer();
          } else {
            closeDrawer();
          }
        }
      });

      function handleClickOutside(e) {
        if(projectDrawer.classList.contains('project-drawer--open') && !e.target.matches('.project-drawer, .project-drawer *')) {
          projectDrawer.classList.remove('project-drawer--open');
          closeDrawer();
          // e.preventDefault();
        }
      }

      document.addEventListener('click', handleClickOutside);

      function handleEsc(e) {
        if(projectDrawer.classList.contains('project-drawer--open') && e.key == 'Escape') {
          projectDrawer.classList.remove('project-drawer--open');
          closeDrawer();
        }
      }

      document.addEventListener('keydown', handleEsc);

    }

  }

}

// --------------------------------------------------
// Services
// --------------------------------------------------

const servicesHorizontalScroll = () => {
  let servicesWrap = document.querySelector('.services');
  if(servicesWrap) {
    let services = servicesWrap.querySelectorAll('.service');
  
    mm.add("(min-width: 701px)", () => {
      gsap.to(services, {
        x: () => (services[0].offsetWidth + 12) * (services.length - 4) * -1,
        ease: 'none',
        scrollTrigger: {
          trigger: servicesWrap,
          invalidateOnRefresh: true,
          pin: true,
          scrub: true,
          start: 'top-=40 top',
          end: 'bottom top',
        }
      });
    })
  }
}

// --------------------------------------------------
// 🤮 Equal height mobile project teasers
// --------------------------------------------------

const equalHeightProjectTeasers = () => {

  let projectTeaserWrap = document.querySelector('.t_work .project-teasers');

  if(projectTeaserWrap) {

    let projectTeasers = projectTeaserWrap.querySelectorAll('.project-teaser');

    mm.add("(max-width: 600px)", () => {

      gsap.set(projectTeaserWrap, {
        alignItems: 'end',
      })

      function equalHeight() {

        let c = 0;
        projectTeasers.forEach(projectTeaser => {
          c++;
          if((c % 2) == 1) {

            let projectTeaserSibling = projectTeaser.nextElementSibling;
            if(projectTeaserSibling) {

              let projectTeaserImage = projectTeaser.querySelector('.project-teaser__image');
              let projectTeaserSiblingImage = projectTeaserSibling.querySelector('.project-teaser__image');

              gsap.set([projectTeaserImage, projectTeaserSiblingImage], {
                clearProps: 'height',
              })

              if(projectTeaserImage.offsetHeight > projectTeaserSiblingImage.offsetHeight) {
                gsap.set(projectTeaserSiblingImage, {
                  height: projectTeaserImage.offsetHeight,
                })
              }
              else {
                gsap.set(projectTeaserImage, {
                  height: projectTeaserSiblingImage.offsetHeight,
                })
              }

            }

          }
        })

      }

      equalHeight();

      ScrollTrigger.addEventListener("refresh", equalHeight);

      // Cleanup
      return () => {
        ScrollTrigger.removeEventListener("refresh", equalHeight);
        gsap.set('.project-teaser__image', {
          clearProps: 'height',
        })
      };

    });

  }

}

// --------------------------------------------------
// ✨ Page Transitions
// --------------------------------------------------

let currentLogoAnimation;
let currentProjectSlider;

let header = document.querySelector('.header');

const updateHeader = (currentContainer, nextContainer, animate = true) => {

  let oldPageState = {
    backButton: false
  };

  if(currentContainer) {
    oldPageState = JSON.parse(currentContainer.dataset.state);
  }

  let newPageState = JSON.parse(nextContainer.dataset.state);

  // --------------------------------------------------
  // Animate back button
  // --------------------------------------------------

  let state = Flip.getState('.header .logo, .header .ticker, .header .menu-buttons .button');

  header.classList.toggle('header--back-button', newPageState.backButton);

  if(animate && oldPageState.backButton != newPageState.backButton) {
    Flip.from(state, {
      duration: .25,
      ease: 'power1.inOut',
      onEnter: elements => gsap.fromTo(elements, {autoAlpha: 0}, {autoAlpha: 1, ease: 'none', duration: .25, delay: .1}),
      onLeave: elements => gsap.to(elements, {autoAlpha: 0, ease: 'none', duration: .25}),  
    })
  }

  // --------------------------------------------------
  // Update href of language toggle
  // --------------------------------------------------

  languageToggle.setAttribute('href', newPageState.otherLanguageLink);

  // --------------------------------------------------
  // Update body url dataset
  // --------------------------------------------------

  console.log(newPageState);

  document.body.dataset.url = newPageState.url;

}

barba.hooks.beforeEnter((data) => {
  window.scrollTo(0, 0);
  updateHeader(data.current.container, data.next.container);
});

barba.init({
  // debug: true,
  // preventRunning: false,
  views: [
    {
      namespace: "home",
      beforeEnter(data) {

        // Initial page lad
        if (!data.current.container) {
          new HomeLoadAnimation(data.next.container.querySelectorAll('.project-teaser'));
        }

        // After the initial page load
        else {
          new HomeLoadAnimation(data.next.container.querySelectorAll('.project-teaser'), true);
        }

        // Always
        lazyLoading();
        currentLogoAnimation = new bigLogoAnimation();

      },
      beforeLeave(data) {
        currentLogoAnimation.kill();
        mm.revert();
        // TODO: Save scroll position?
      },
    },
    {
      namespace: "about",
      beforeEnter(data) {
        new DefaultLoadAnimation();
        lazyLoading();
        currentLogoAnimation = new bigLogoAnimation();
        servicesHorizontalScroll();
      },
      beforeLeave(data) {
        currentLogoAnimation.kill();
        mm.revert();
      },
    },
    {
      namespace: "default",
      beforeEnter(data) {
        new DefaultLoadAnimation();
        lazyLoading();
        currentLogoAnimation = new bigLogoAnimation();
      },
      beforeLeave(data) {
        currentLogoAnimation.kill();
        mm.revert();
      },
    },
    {
      namespace: "project",
      beforeEnter(data) {
        new DefaultLoadAnimation();
        document.documentElement.classList.add('horizontal');
        currentProjectSlider = new ProjectSlider(data.next.container);
        new ProjectDrawer(data.next.container);
        setTimeout(() => lazyLoading(), 250);
      },
      beforeLeave(data) {
        document.documentElement.classList.remove('horizontal');
        currentProjectSlider.kill();
        mm.revert();
      }
    },
    {
      namespace: "work",
      beforeEnter(data) {
        new DefaultLoadAnimation();
        filterBar();
        new Filter();
        equalHeightProjectTeasers();
        setTimeout(() => lazyLoading(), 250);
      },
      beforeLeave(data) {
        mm.revert();
      }
    },
  ],
});
